import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Loader2, BookTemplate, Clock, AlertTriangle,
  AlertCircle as AlertIcon, X, ListChecks, Sparkles, Tag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Priority configuration
const priorityConfig = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-700' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  emergency: { label: 'Emergency', color: 'bg-red-100 text-red-700' }
};

// Case types for selection
const CASE_TYPES = [
  { id: 'personal_injury', name: 'Personal Injury' },
  { id: 'family_law', name: 'Family Law' },
  { id: 'employment', name: 'Employment' },
  { id: 'business_litigation', name: 'Business Litigation' },
  { id: 'real_estate', name: 'Real Estate' },
  { id: 'criminal', name: 'Criminal Defense' },
  { id: 'estate_planning', name: 'Estate Planning' },
  { id: 'bankruptcy', name: 'Bankruptcy' },
  { id: 'immigration', name: 'Immigration' },
  { id: 'intellectual_property', name: 'Intellectual Property' }
];

export default function TaskTemplatesManager() {
  const { token, user } = useAuth();
  
  // State
  const [templates, setTemplates] = useState({ system_templates: [], custom_templates: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    task_type: 'other',
    priority: 'medium',
    default_due_days: 7,
    estimated_hours: '',
    case_types: [],
    category: '',
    checklist: [],
    notes: ''
  });
  
  // Checklist input
  const [checklistInput, setChecklistInput] = useState('');

  // Check if user is manager
  const isManager = ['owner', 'manager', 'admin'].includes(user?.role);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/task-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setTemplates(await res.json());
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Reset form
  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      task_type: 'other',
      priority: 'medium',
      default_due_days: 7,
      estimated_hours: '',
      case_types: [],
      category: '',
      checklist: [],
      notes: ''
    });
    setEditingTemplate(null);
    setChecklistInput('');
  };

  // Open create dialog
  const handleCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  // Open edit dialog
  const handleEdit = (template) => {
    if (template.is_system) {
      toast.error('System templates cannot be edited');
      return;
    }
    
    setEditingTemplate(template);
    setForm({
      name: template.name,
      description: template.description || '',
      task_type: template.task_type || 'other',
      priority: template.priority || 'medium',
      default_due_days: template.default_due_days || 7,
      estimated_hours: template.estimated_hours || '',
      case_types: template.case_types || [],
      category: template.category || '',
      checklist: template.checklist || [],
      notes: template.notes || ''
    });
    setShowDialog(true);
  };

  // Save template
  const handleSave = async () => {
    if (!form.name) {
      toast.error('Please enter a template name');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('description', form.description);
      formData.append('task_type', form.task_type);
      formData.append('priority', form.priority);
      formData.append('default_due_days', form.default_due_days || 7);
      if (form.estimated_hours) formData.append('estimated_hours', form.estimated_hours);
      formData.append('case_types', JSON.stringify(form.case_types));
      formData.append('category', form.category);
      formData.append('checklist', JSON.stringify(form.checklist));
      formData.append('notes', form.notes);

      const url = editingTemplate 
        ? `${API_URL}/api/case-management/task-templates/${editingTemplate.id}`
        : `${API_URL}/api/case-management/task-templates`;
      
      const res = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success(editingTemplate ? 'Template updated' : 'Template created');
        setShowDialog(false);
        fetchTemplates();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const handleDelete = async (templateId, isSystem) => {
    if (isSystem) {
      toast.error('System templates cannot be deleted');
      return;
    }
    
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/case-management/task-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete template');
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  // Add checklist item
  const addChecklistItem = () => {
    if (!checklistInput.trim()) return;
    setForm(prev => ({
      ...prev,
      checklist: [...prev.checklist, checklistInput.trim()]
    }));
    setChecklistInput('');
  };

  // Remove checklist item
  const removeChecklistItem = (index) => {
    setForm(prev => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  };

  // Toggle case type
  const toggleCaseType = (typeId) => {
    setForm(prev => ({
      ...prev,
      case_types: prev.case_types.includes(typeId)
        ? prev.case_types.filter(t => t !== typeId)
        : [...prev.case_types, typeId]
    }));
  };

  // Filter templates
  const getFilteredTemplates = () => {
    const all = [...templates.system_templates, ...templates.custom_templates];
    if (selectedCategory === 'all') return all;
    return all.filter(t => t.category === selectedCategory);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="task-templates-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Task Templates</h2>
          <p className="text-sm text-slate-500">
            {templates.system_templates.length} system + {templates.custom_templates.length} custom templates
          </p>
        </div>
        {isManager && (
          <Button onClick={handleCreate} data-testid="create-template-btn">
            <Plus className="w-4 h-4 mr-1" />
            New Template
          </Button>
        )}
      </div>

      {/* Category filter */}
      <div className="flex items-center gap-4">
        <Label className="text-sm text-slate-500">Filter by category:</Label>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {templates.categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Templates grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {getFilteredTemplates().map(template => (
          <Card key={template.id} className="relative">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <BookTemplate className="w-4 h-4 text-indigo-600" />
                  <CardTitle className="text-base">{template.name}</CardTitle>
                </div>
                {template.is_system ? (
                  <Badge variant="outline" className="text-xs bg-slate-100">System</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700">Custom</Badge>
                )}
              </div>
              <CardDescription className="text-xs line-clamp-2">
                {template.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={`text-[10px] ${priorityConfig[template.priority]?.color}`}>
                    {priorityConfig[template.priority]?.label}
                  </Badge>
                  {template.category && (
                    <Badge variant="outline" className="text-[10px]">{template.category}</Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  {template.default_due_days && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {template.default_due_days}d default
                    </span>
                  )}
                  {template.estimated_hours && (
                    <span>{template.estimated_hours}h est.</span>
                  )}
                </div>

                {template.case_types?.length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap">
                    <Tag className="w-3 h-3 text-slate-400" />
                    {template.case_types.slice(0, 2).map(t => (
                      <span key={t} className="text-[10px] text-slate-500 bg-slate-100 px-1 rounded">
                        {t.replace('_', ' ')}
                      </span>
                    ))}
                    {template.case_types.length > 2 && (
                      <span className="text-[10px] text-slate-400">+{template.case_types.length - 2}</span>
                    )}
                  </div>
                )}

                {template.checklist?.length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <ListChecks className="w-3 h-3" />
                    {template.checklist.length} checklist items
                  </div>
                )}
              </div>

              {/* Actions */}
              {isManager && !template.is_system && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(template)}>
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDelete(template.id, template.is_system)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {getFilteredTemplates().length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <BookTemplate className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <p className="text-lg font-medium">No templates found</p>
          <p className="text-sm">
            {selectedCategory !== 'all' 
              ? 'Try selecting a different category' 
              : 'Create your first custom template'}
          </p>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookTemplate className="w-5 h-5 text-indigo-600" />
              {editingTemplate ? 'Edit Template' : 'New Task Template'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate 
                ? 'Update this task template'
                : 'Create a reusable template for common tasks'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <Label htmlFor="name">Template Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Request Medical Records"
                data-testid="template-name-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this task involves..."
                rows={2}
              />
            </div>

            {/* Task Type and Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Task Type</Label>
                <Select
                  value={form.task_type}
                  onValueChange={(v) => setForm(prev => ({ ...prev, task_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft_document">Draft Document</SelectItem>
                    <SelectItem value="research">Legal Research</SelectItem>
                    <SelectItem value="client_call">Client Call</SelectItem>
                    <SelectItem value="client_meeting">Client Meeting</SelectItem>
                    <SelectItem value="court_filing">Court Filing</SelectItem>
                    <SelectItem value="review_document">Review Document</SelectItem>
                    <SelectItem value="send_correspondence">Send Correspondence</SelectItem>
                    <SelectItem value="prepare_discovery">Prepare Discovery</SelectItem>
                    <SelectItem value="administrative">Administrative</SelectItem>
                    <SelectItem value="follow_up">Follow Up</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Default Priority</Label>
                <Select
                  value={form.priority}
                  onValueChange={(v) => setForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Due Days and Estimated Hours */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="default_due_days">Default Due (days from creation)</Label>
                <Input
                  id="default_due_days"
                  type="number"
                  min="1"
                  value={form.default_due_days}
                  onChange={(e) => setForm(prev => ({ ...prev, default_due_days: parseInt(e.target.value) || 7 }))}
                />
              </div>
              <div>
                <Label htmlFor="estimated_hours">Estimated Hours</Label>
                <Input
                  id="estimated_hours"
                  type="number"
                  step="0.25"
                  value={form.estimated_hours}
                  onChange={(e) => setForm(prev => ({ ...prev, estimated_hours: e.target.value }))}
                  placeholder="e.g., 2.5"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={form.category}
                onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g., Discovery, Intake, Investigation"
              />
            </div>

            {/* Case Types */}
            <div>
              <Label>Applicable Case Types (leave empty for all)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 p-3 border rounded-lg max-h-40 overflow-y-auto">
                {CASE_TYPES.map(type => (
                  <div key={type.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`case-type-${type.id}`}
                      checked={form.case_types.includes(type.id)}
                      onCheckedChange={() => toggleCaseType(type.id)}
                    />
                    <Label htmlFor={`case-type-${type.id}`} className="cursor-pointer text-sm">
                      {type.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Checklist */}
            <div>
              <Label>Checklist Items</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  value={checklistInput}
                  onChange={(e) => setChecklistInput(e.target.value)}
                  placeholder="Add a checklist item..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                />
                <Button type="button" variant="outline" onClick={addChecklistItem}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {form.checklist.length > 0 && (
                <div className="mt-2 space-y-1">
                  {form.checklist.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded">
                      <ListChecks className="w-4 h-4 text-slate-400" />
                      <span className="flex-1 text-sm">{item}</span>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeChecklistItem(index)}
                        className="h-6 w-6 p-0 text-slate-400 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Internal Notes</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes for this template..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                editingTemplate ? 'Update Template' : 'Create Template'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
